<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useFlowBuilderStore } from '../store/index.js';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import { TRIGGER_EVENTS, NODE_CONFIG } from '../constants/nodeTypes.js';
import FlowBuilderAPI from '../api/index.js';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useFlowBuilderStore();
const { accountScopedRoute } = useAccount();
const { formatDate: formatWithLocale } = useDateLocale();

const PER_PAGE = 20;

const executions = ref([]);
const loading = ref(true);
const expandedId = ref(null);
const currentPage = ref(1);
const totalCount = ref(0);

const steps = ref([]);
const stepsMeta = ref({ total: 0, current_page: 1, per_page: 15, total_pages: 1 });
const stepsLoading = ref(false);

const showPagination = computed(() => totalCount.value > PER_PAGE);
const showStepsPagination = computed(() => stepsMeta.value.total_pages > 1);

const triggerLabel = computed(() => Object.fromEntries(TRIGGER_EVENTS.map(e => [e.key, t(e.label)])));

async function fetchPage(page) {
  loading.value = true;
  try {
    const res = await FlowBuilderAPI.getExecutions(route.params.flowId, page);
    executions.value = res.data.executions ?? [];
    totalCount.value = res.data.meta?.total_count ?? 0;
    currentPage.value = page;
    expandedId.value = null;
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await store.fetchFlow(route.params.flowId);
  await fetchPage(1);
});

const flowName = computed(() => store.currentFlow?.name ?? '');

function goBack() {
  router.push(accountScopedRoute('flow_editor', { flowId: route.params.flowId }));
}

function onBreadcrumbClick(_item, index) {
  // index 0 = "Fluxos" → list; index 1 = flow name → editor.
  // Last index (Execuções) is rendered as plain text and never clickable.
  const routeName = index === 0 ? 'flow_list' : 'flow_editor';
  const params = index === 0 ? {} : { flowId: route.params.flowId };
  router.push(accountScopedRoute(routeName, params));
}

async function fetchSteps(executionId, page = 1) {
  stepsLoading.value = true;
  try {
    const res = await FlowBuilderAPI.getExecutionSteps(route.params.flowId, executionId, page);
    steps.value = res.data.steps ?? [];
    stepsMeta.value = res.data.meta ?? stepsMeta.value;
  } finally {
    stepsLoading.value = false;
  }
}

async function toggleExpanded(id) {
  if (expandedId.value === id) {
    expandedId.value = null;
    steps.value = [];
    stepsMeta.value = { total: 0, current_page: 1, per_page: 15, total_pages: 1 };
  } else {
    expandedId.value = id;
    await fetchSteps(id);
  }
}

function changeStepsPage(page) {
  fetchSteps(expandedId.value, page);
}

function fmtDate(iso) {
  if (!iso) return '—';
  return formatWithLocale(iso, 'dd/MM/yy HH:mm');
}

function fmtDuration(s) {
  if (s == null) return '—';
  if (s < 1) return `${Math.round(s * 1000)}ms`;
  return `${s}s`;
}

const STATUS_META = computed(() => ({
  completed: { label: t('FLOW_BUILDER.EXECUTIONS.STATUS.COMPLETED'), icon: 'i-lucide-circle-check', color: 'text-n-teal-11',  bg: 'bg-n-teal-3' },
  failed:    { label: t('FLOW_BUILDER.EXECUTIONS.STATUS.FAILED'),    icon: 'i-lucide-circle-x',     color: 'text-n-ruby-11',  bg: 'bg-n-ruby-3' },
  running:   { label: t('FLOW_BUILDER.EXECUTIONS.STATUS.RUNNING'),   icon: 'i-lucide-loader',       color: 'text-n-blue-11',  bg: 'bg-n-blue-3' },
  pending:   { label: t('FLOW_BUILDER.EXECUTIONS.STATUS.PENDING'),  icon: 'i-lucide-clock',        color: 'text-n-slate-9',  bg: 'bg-n-alpha-2' },
}));

const STEP_META = {
  ok:        { icon: 'i-lucide-check', color: 'text-n-teal-11',  ringClass: 'ring-n-teal-5' },
  error:     { icon: 'i-lucide-x',     color: 'text-n-ruby-11',  ringClass: 'ring-n-ruby-5' },
  scheduled: { icon: 'i-lucide-clock', color: 'text-n-amber-11', ringClass: 'ring-n-amber-5' },
  skipped:   { icon: 'i-lucide-minus', color: 'text-n-slate-9',  ringClass: 'ring-n-slate-4' },
};

function stepLabel(step) {
  if (step.data?.action_name) return step.data.action_name.replace(/_/g, ' ');
  return NODE_CONFIG[step.node_type]?.label ? t(NODE_CONFIG[step.node_type].label) : step.node_type;
}

function stepMeta(step) {
  return STEP_META[step.status] ?? STEP_META.skipped;
}
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-hidden bg-n-surface-1 font-inter">

    <!-- Header -->
    <header class="sticky top-0 z-10 px-6 lg:px-16 bg-n-surface-1">
      <div class="flex items-center w-full py-6 gap-4 mx-auto max-w-7xl">
        <Breadcrumb
          :items="[
            { label: t('FLOW_BUILDER.EXECUTIONS.BREADCRUMB_FLOWS') },
            { label: flowName },
            { label: t('FLOW_BUILDER.EXECUTIONS.BREADCRUMB_EXECUTIONS') },
          ]"
          @click="onBreadcrumbClick"
        />
      </div>
    </header>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto px-6 lg:px-16 scrollbar-custom flex justify-center">
      <div class="w-full max-w-7xl">

        <!-- Skeleton -->
        <div v-if="loading" class="flex flex-col gap-4 pb-6 animate-pulse">
          <div
            v-for="n in 5"
            :key="n"
            class="flex items-center gap-4 px-6 py-5 outline outline-1 outline-n-container -outline-offset-1 rounded-xl bg-n-solid-2"
          >
            <div class="h-6 w-24 rounded-full bg-n-alpha-2 shrink-0" />
            <div class="flex flex-col gap-1.5 flex-1">
              <div class="h-3.5 w-40 rounded bg-n-alpha-2" />
              <div class="h-3 w-24 rounded bg-n-alpha-2" />
            </div>
            <div class="hidden sm:flex items-center gap-4">
              <div class="h-3 w-16 rounded bg-n-alpha-2" />
              <div class="h-3 w-12 rounded bg-n-alpha-2" />
              <div class="h-3 w-28 rounded bg-n-alpha-2" />
            </div>
            <div class="h-4 w-4 rounded bg-n-alpha-2 shrink-0" />
          </div>
        </div>

        <!-- Empty -->
        <div v-else-if="executions.length === 0" class="flex items-center justify-center py-20">
          <div class="flex flex-col items-center gap-4 max-w-md text-center">
            <div class="flex h-16 w-16 items-center justify-center rounded-2xl bg-n-slate-3">
              <i class="i-lucide-activity w-8 h-8 text-n-slate-11" />
            </div>
            <div class="flex flex-col gap-2">
              <h3 class="text-lg font-medium text-n-slate-12">
                {{ t('FLOW_BUILDER.EXECUTIONS.EMPTY.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ t('FLOW_BUILDER.EXECUTIONS.EMPTY.DESCRIPTION') }}
              </p>
            </div>
            <Button
              icon="i-lucide-arrow-left"
              size="sm"
              variant="ghost"
              color="slate"
              @click="goBack"
            >
              {{ t('FLOW_BUILDER.EXECUTIONS.EMPTY.BACK_TO_EDITOR') }}
            </Button>
          </div>
        </div>

        <!-- List -->
        <div v-else class="flex flex-col gap-4 pb-6">
          <CardLayout
            v-for="ex in executions"
            :key="ex.id"
            layout="row"
          >
            <!-- Row content -->
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0"
              :class="[
                STATUS_META[ex.status]?.bg,
                STATUS_META[ex.status]?.color,
              ]"
            >
              <span :class="[STATUS_META[ex.status]?.icon, 'size-3.5']" />
              {{ STATUS_META[ex.status]?.label }}
            </span>

            <div class="flex flex-col gap-0.5 min-w-0 flex-1">
              <div class="flex items-center gap-1.5 text-sm text-n-slate-12 min-w-0">
                <span class="i-lucide-zap size-3.5 shrink-0 text-n-slate-10" />
                <span class="truncate">{{ triggerLabel[ex.trigger_event] ?? ex.trigger_event }}</span>
              </div>
              <div v-if="ex.contact_name" class="flex items-center gap-1.5 text-xs text-n-slate-10 min-w-0">
                <span class="i-lucide-user size-3 shrink-0" />
                <span class="truncate">{{ ex.contact_name }}</span>
              </div>
            </div>

            <div class="hidden sm:flex items-center gap-4 flex-shrink-0 text-xs text-n-slate-10">
              <span class="flex items-center gap-1">
                <span class="i-lucide-footprints size-3" />
                {{
                  t(
                    'FLOW_BUILDER.EXECUTIONS.STEPS_COUNT',
                    { count: ex.steps_count ?? 0 },
                    ex.steps_count ?? 0
                  )
                }}
              </span>
              <span class="flex items-center gap-1">
                <span class="i-lucide-timer size-3" />
                {{ fmtDuration(ex.duration_seconds) }}
              </span>
              <span class="flex items-center gap-1">
                <span class="i-lucide-calendar size-3" />
                {{ fmtDate(ex.created_at) }}
              </span>
            </div>

            <Button
              icon="i-lucide-chevron-down"
              variant="ghost"
              color="slate"
              size="xs"
              :class="{ 'rotate-180': expandedId === ex.id }"
              @click="toggleExpanded(ex.id)"
            />

            <!-- Expanded detail -->
            <template #after>
              <div
                class="transition-all duration-500 ease-in-out grid overflow-hidden"
                :class="expandedId === ex.id ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'"
              >
                <div class="overflow-hidden">
                  <div class="flex flex-col gap-4 p-6 border-t border-n-strong">

                    <div
                      v-if="ex.error_message"
                      class="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-n-ruby-3 border border-n-ruby-5 text-xs text-n-ruby-11"
                    >
                      <span class="i-lucide-triangle-alert size-3.5 mt-0.5 shrink-0" />
                      {{ ex.error_message }}
                    </div>

                    <div>
                      <p class="py-1 text-sm font-medium text-n-slate-12 mb-4">
                        {{ t('FLOW_BUILDER.EXECUTIONS.STEPS_TITLE') }}
                      </p>
                      <div class="flex flex-col w-full">
                        <div
                          v-if="stepsLoading"
                          class="flex flex-col gap-3 animate-pulse py-2"
                        >
                          <div v-for="n in 5" :key="n" class="h-6 w-full rounded bg-n-alpha-2" />
                        </div>
                        <template v-else>
                          <div
                            v-for="(step, i) in steps"
                            :key="i"
                            class="flex gap-3"
                          >
                            <div class="flex flex-col items-center">
                              <div
                                class="flex items-center justify-center size-6 rounded-full ring-2 bg-n-solid-1 shrink-0"
                                :class="stepMeta(step).ringClass"
                              >
                                <span :class="[stepMeta(step).icon, stepMeta(step).color, 'size-3']" />
                              </div>
                              <div
                                v-if="!(i === steps.length - 1 && stepsMeta.current_page >= stepsMeta.total_pages)"
                                class="w-px flex-1 bg-n-weak min-h-[20px]"
                              />
                            </div>

                            <div class="pb-4 min-w-0 flex-1">
                              <div class="flex items-center gap-2 flex-wrap">
                                <span class="text-sm font-medium text-n-slate-12 capitalize">
                                  {{ stepLabel(step) }}
                                </span>
                                <span class="text-[10px] text-n-slate-9 ml-auto">
                                  {{ fmtDate(step.timestamp) }}
                                </span>
                              </div>
                              <div v-if="step.data && Object.keys(step.data).length" class="mt-1">
                                <pre class="text-[10px] text-n-slate-9 bg-n-alpha-2 rounded px-2 py-1.5 overflow-x-auto">{{ JSON.stringify(step.data, null, 2) }}</pre>
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>

                  </div>
                  <PaginationFooter
                    v-if="showStepsPagination && ex.id === expandedId"
                    :current-page="stepsMeta.current_page"
                    :total-items="stepsMeta.total"
                    :items-per-page="stepsMeta.per_page"
                    @update:current-page="changeStepsPage"
                  />
                </div>
              </div>
            </template>
          </CardLayout>
        </div>

      </div>
    </div>

    <!-- Pagination -->
    <PaginationFooter
      v-if="showPagination"
      :current-page="currentPage"
      :total-items="totalCount"
      :items-per-page="PER_PAGE"
      @update:current-page="fetchPage"
    />

  </div>
</template>
